import {
  Component,
  DestroyRef,
  Input,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadCrumb } from '../../core/types';
import { BreadcrumbService } from '../../services/breadcrumb.service';
import { EMPTY, Observable, filter, merge, of } from 'rxjs';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
} from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

/**
 * Legacy layout breadcrumb strip. Hidden on public Schulprofil (`/schools/:id`) because that
 * screen uses `app-school-authority-breadcrumb` in-page; clearing the service alone is not
 * enough (router `firstChild` vs leaf timing).
 */
@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  imports: [RouterLink, MatIcon, AsyncPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class BreadcrumbsComponent {
  @Input() crumbs$: Observable<BreadCrumb[]> = EMPTY;
  renderElement = false;

  /** When false, do not render the layout strip (e.g. public `/schools/:id`). */
  readonly legacyStripAllowed = signal(true);
  readonly navigating = signal(false);

  private readonly router = inject(Router);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.crumbs$ = this.breadcrumbService.breadcrumbs$;
    this.crumbs$.subscribe(crumbs => {
      this.renderElement = crumbs.length > 0;
    });

    const syncPublicSchoolChrome = () => {
      const path = this.router.url.split('?')[0];
      const hide = this.isPublicSchoolProfilePath(path);
      this.legacyStripAllowed.set(!hide);
      if (hide) {
        this.breadcrumbService.clear();
      }
    };

    merge(
      of(null),
      this.router.events.pipe(
        filter(
          (e): e is NavigationStart | NavigationEnd =>
            e instanceof NavigationStart || e instanceof NavigationEnd
        )
      )
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(e => {
        if (e instanceof NavigationStart) {
          this.navigating.set(true);
        }
        if (e instanceof NavigationEnd) {
          this.navigating.set(false);
        }
        syncPublicSchoolChrome();
      });
  }

  /** Exactly `/schools/:id` (öffentliches Profil), not list, subscription, edit, onboarding. */
  private isPublicSchoolProfilePath(path: string): boolean {
    const m = path.match(/^\/schools\/([^/]+)$/);
    if (!m) return false;
    return m[1] !== 'subscription';
  }

  showSeperator(index: number, crumbs: BreadCrumb[]) {
    const l = crumbs.length;
    return l > 1 && (index === 0 || index < l - 1);
  }
}
